// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Bootstrap: resolve the pairing (from the link hash, a remembered pairing, or
 * freshly minted), join the room, and wire the transport to the UI. Sending and
 * receiving items runs on the main thread — each item is a small AES-GCM blob, so
 * a worker would only add latency; the transport itself is already off-thread
 * inside trystero.
 */
import './styles/main.css';
import QRCode from 'qrcode';
import { AppUI } from './ui.ts';
import { initModals, toast } from './modals.ts';
import { initGlossary } from './glossary.ts';
import { emit } from './eventlog.ts';
import { readInvite, writeRoomHash, currentInviteUrl } from './router.ts';
import { generateRoomId, generatePassphrase, normalizePass, parseInvite } from './room.ts';
import { deriveItemKey, deriveSasWords, encryptBytes, decryptBytes } from './crypto.ts';
import { encodeEnvelope, decodeEnvelope, packFrame, unpackFrame } from './envelope.ts';
import { classifyText, makeItemId, imageFilename } from './items.ts';
import { BeamSession, type Hello } from './session.ts';
import { QrScanner, supportsScan } from './scan.ts';
import {
  loadPairings, savePairings, upsertPairing, removePairing, touchPairing, makePairing,
  type StorageLike,
} from './pairing.ts';
import type { BeamItem, Pairing, Status } from './types.ts';

const LS_NAME = 'beamwell.name';
const LS_REMEMBER = 'beamwell.remember';
const MAX_IMAGE_BYTES = 20 * 1024 * 1024; // beamwell is a clipboard bridge, not a file mover

let room = '';
let pass = '';
let deviceName = '';
let remember = false;

let session: BeamSession | null = null;
let itemKey: CryptoKey | null = null;
let primaryPeer: string | null = null;
let peerName = '';
const extraPeers = new Set<string>();

const items = new Map<string, BeamItem>();
let staged: { bytes: Uint8Array; name: string; mime: string; url: string } | null = null;

const scanner = new QrScanner();
const store: StorageLike = safeStorage();
let pairings: Pairing[] = [];

const ui = new AppUI(document.getElementById('app') as HTMLElement, {
  onCopyLink: copyLink,
  onShareLink: shareLink,
  onStartScan: startScan,
  onStopScan: stopScan,
  onConnectSaved: connectSaved,
  onForgetSaved: forgetSaved,
  onNameChange: (n) => {
    deviceName = n;
    store.setItem(LS_NAME, n);
  },
  onRememberChange: (on) => {
    remember = on;
    store.setItem(LS_REMEMBER, on ? '1' : '0');
    if (on && primaryPeer) rememberCurrent();
  },
  onBeamText: beam,
  onBeamClipboard: beamClipboard,
  onBeamImageFiles: (files) => void stageImage(files[0]),
  onDisconnect: disconnect,
  onCopyItem: copyItem,
  onSaveItem: saveItem,
  onShareItem: shareItem,
  onClearFeed: clearFeed,
});

init();

function init(): void {
  initModals();
  initGlossary();

  deviceName = store.getItem(LS_NAME) || '';
  remember = store.getItem(LS_REMEMBER) === '1';
  pairings = loadPairings(store);
  ui.setName(deviceName);
  ui.setRemember(remember);
  ui.setSupportsScan(supportsScan());

  const invite = readInvite();
  if (invite && invite.pass) {
    room = invite.room;
    pass = normalizePass(invite.pass);
  } else {
    if (invite && !invite.pass) toast('That link was missing its passphrase — made a fresh pairing.', 'err');
    room = generateRoomId();
    pass = generatePassphrase();
    writeRoomHash(room, pass, true);
  }

  window.addEventListener('pagehide', () => {
    scanner.stop();
    session?.leave();
  });

  void connect();
  installTestHook();
}

async function connect(): Promise<void> {
  if (!pass) {
    toast('This pairing is missing its passphrase.', 'err');
    return;
  }
  const url = currentInviteUrl(room, pass, true);
  ui.setInviteLink(url);
  void renderQr(url);
  void renderSas();
  ui.renderSaved(pairings, room);
  ui.showPairing(true);
  ui.showSession(false);
  ui.setStatus('waiting');

  try {
    itemKey = await deriveItemKey(pass, room);
  } catch (e) {
    emit('bad', `Key derivation failed: ${(e as Error).message}`);
    return;
  }

  session = new BeamSession(room, pass, {
    onPeerJoin: handlePeerJoin,
    onPeerLeave: handlePeerLeave,
    onHello: handleHello,
    onFrame: handleFrame,
    onFrameProgress: (_p, _prog, _id) => undefined,
    onAck: handleAck,
    onError: (m) => {
      emit('bad', m);
      toast(m, 'err');
      ui.setStatus('error');
    },
  });
  const ok = session.join();
  if (ok) emit('info', 'Ready. Open this link or scan the QR on your other device to pair.');
}

function reconnect(): void {
  teardownSession();
  void connect();
}

function teardownSession(): void {
  session?.leave();
  session = null;
  itemKey = null;
  primaryPeer = null;
  peerName = '';
  extraPeers.clear();
  ui.showExtraPeer(false);
}

// ── transport handlers ──────────────────────────────────────────────────────
function handlePeerJoin(peerId: string): void {
  if (!primaryPeer) {
    primaryPeer = peerId;
    session?.sendHello({ name: deviceName || 'A device' }, peerId);
    ui.setStatus('paired', peerName);
    // brief "confirm safety words", then ready
    ui.showPairing(false);
    ui.showSession(true);
    ui.setStatus('ready', peerName);
    ui.focusCompose();
    emit('good', 'Paired with your other device.');
    if (remember) rememberCurrent();
  } else if (peerId !== primaryPeer) {
    extraPeers.add(peerId);
    ui.showExtraPeer(true);
    emit('warn', 'Another device tried to join — Beamwell pairs two devices only.');
  }
}

function handlePeerLeave(peerId: string): void {
  if (peerId === primaryPeer) {
    primaryPeer = null;
    peerName = '';
    ui.setPeer(null);
    ui.setStatus('waiting');
    ui.showPairing(true);
    ui.showSession(false);
    emit('warn', 'Your other device disconnected.');
    ui.renderSaved(pairings, room);
  } else {
    extraPeers.delete(peerId);
    if (extraPeers.size === 0) ui.showExtraPeer(false);
  }
}

function handleHello(peerId: string, hello: Hello): void {
  if (peerId !== primaryPeer) return;
  peerName = (hello?.name || '').trim();
  ui.setPeer(peerName || 'peer');
  if (remember) rememberCurrent();
}

async function handleFrame(peerId: string, frame: Uint8Array): Promise<void> {
  if (peerId !== primaryPeer || !itemKey) return;
  try {
    const { iv, cipher } = unpackFrame(frame);
    const plain = await decryptBytes(itemKey, iv, cipher);
    const { header, payload } = decodeEnvelope(plain);
    const at = typeof header.at === 'number' ? header.at : Date.now();
    let item: BeamItem;
    if (header.kind === 'image') {
      const bytes = payload.slice();
      const mime = header.mime || 'image/jpeg';
      const blob = new Blob([bytes as BlobPart], { type: mime });
      item = {
        id: header.id, dir: 'in', kind: 'image', at, state: 'received',
        name: header.name, mime, size: bytes.length, bytes, blobUrl: URL.createObjectURL(blob),
      };
      emit('good', `Received image (${imageFilename(header.name, mime, at)}).`);
    } else {
      item = { id: header.id, dir: 'in', kind: header.kind, at, state: 'received', text: header.text ?? '' };
      emit('good', `Received ${header.kind}.`);
    }
    items.set(item.id, item);
    ui.upsertItem(item);
    session?.sendAck(item.id, peerId);
    toast('Received on this device.', 'ok');
  } catch (e) {
    emit('bad', `Could not open a received item: ${(e as Error).message}`);
  }
}

function handleAck(peerId: string, id: string): void {
  if (peerId !== primaryPeer) return;
  const item = items.get(id);
  if (item && item.dir === 'out') {
    item.state = 'delivered';
    ui.upsertItem(item);
  }
}

// ── sending ─────────────────────────────────────────────────────────────────
function beam(text: string): void {
  if (!primaryPeer || !itemKey) {
    toast('Pair your other device first.', 'err');
    return;
  }
  const hasImage = !!staged;
  const trimmed = text.trim();
  if (!hasImage && !trimmed) {
    toast('Type something or add an image to beam.', 'err');
    return;
  }
  if (trimmed) {
    void sendText(trimmed);
    ui.setCompose('');
  }
  if (hasImage && staged) {
    void sendImage(staged.bytes, staged.name, staged.mime);
    clearStaged();
  }
}

async function sendText(text: string): Promise<void> {
  const id = makeItemId();
  const at = Date.now();
  const kind = classifyText(text);
  const item: BeamItem = { id, dir: 'out', kind, at, state: 'sending', text };
  items.set(id, item);
  ui.upsertItem(item);
  try {
    const env = encodeEnvelope({ id, kind, at, text });
    await sendEnvelope(id, env);
    markSent(id);
    emit('xfer', `Beamed ${kind}.`);
  } catch (e) {
    markError(id, e as Error);
  }
}

async function sendImage(bytes: Uint8Array, name: string, mime: string): Promise<void> {
  const id = makeItemId();
  const at = Date.now();
  const blob = new Blob([bytes as BlobPart], { type: mime });
  const item: BeamItem = {
    id, dir: 'out', kind: 'image', at, state: 'sending',
    name, mime, size: bytes.length, bytes, blobUrl: URL.createObjectURL(blob),
  };
  items.set(id, item);
  ui.upsertItem(item);
  try {
    const env = encodeEnvelope({ id, kind: 'image', at, name, mime }, bytes);
    await sendEnvelope(id, env);
    markSent(id);
    emit('xfer', `Beamed image (${fmtSize(bytes.length)}).`);
  } catch (e) {
    markError(id, e as Error);
  }
}

async function sendEnvelope(id: string, env: Uint8Array): Promise<void> {
  if (!itemKey || !session || !primaryPeer) throw new Error('not connected');
  const { iv, cipher } = await encryptBytes(itemKey, env);
  const frame = packFrame(iv, cipher);
  await session.sendFrame(primaryPeer, frame, id);
}

function markSent(id: string): void {
  const item = items.get(id);
  if (item && item.state === 'sending') {
    item.state = 'sent';
    ui.upsertItem(item);
  }
}
function markError(id: string, e: Error): void {
  const item = items.get(id);
  if (item) {
    item.state = 'error';
    ui.upsertItem(item);
  }
  emit('bad', `Beam failed: ${e.message}`);
  toast('Beam failed — try again.', 'err');
}

// ── clipboard + images ──────────────────────────────────────────────────────
async function beamClipboard(): Promise<void> {
  if (!primaryPeer) {
    toast('Pair your other device first.', 'err');
    return;
  }
  try {
    const nav = navigator as Navigator & { clipboard?: Clipboard };
    if (nav.clipboard?.read) {
      const items_ = await nav.clipboard.read();
      for (const it of items_) {
        const imgType = it.types.find((t) => t.startsWith('image/'));
        if (imgType) {
          const blob = await it.getType(imgType);
          const bytes = new Uint8Array(await blob.arrayBuffer());
          void sendImage(bytes, `clipboard.${extFor(imgType)}`, imgType);
          toast('Beamed image from clipboard.', 'ok');
          return;
        }
      }
    }
    const text = await navigator.clipboard.readText();
    if (text && text.trim()) {
      void sendText(text.trim());
      toast('Beamed clipboard text.', 'ok');
    } else {
      toast('Clipboard is empty.', 'err');
    }
  } catch {
    toast('Couldn’t read the clipboard — paste into the box and Beam.', 'err');
  }
}

async function stageImage(file: File | undefined): Promise<void> {
  if (!file) return;
  if (!file.type.startsWith('image/')) {
    toast('Only images can be staged here.', 'err');
    return;
  }
  if (file.size > MAX_IMAGE_BYTES) {
    toast(`Image is too large (max ${fmtSize(MAX_IMAGE_BYTES)}). Use Dropwell for big files.`, 'err');
    return;
  }
  const bytes = new Uint8Array(await file.arrayBuffer());
  const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: file.type }));
  clearStaged();
  staged = { bytes, name: file.name || `image.${extFor(file.type)}`, mime: file.type, url };
  ui.setStaged({ url, name: staged.name });
}

function clearStaged(): void {
  if (staged) URL.revokeObjectURL(staged.url);
  staged = null;
  ui.setStaged(null);
}

// ── item actions ────────────────────────────────────────────────────────────
async function copyItem(id: string): Promise<void> {
  const item = items.get(id);
  if (!item || item.kind === 'image' || !item.text) return;
  try {
    await navigator.clipboard.writeText(item.text);
    toast('Copied to clipboard.', 'ok');
  } catch {
    toast('Could not copy — select the text manually.', 'err');
  }
}

async function saveItem(id: string): Promise<void> {
  const item = items.get(id);
  if (!item || item.kind !== 'image' || !item.bytes) return;
  const name = imageFilename(item.name, item.mime, item.at);
  const blob = new Blob([item.bytes as BlobPart], { type: item.mime || 'image/jpeg' });
  const picker = (window as unknown as { showSaveFilePicker?: (o: unknown) => Promise<FileSystemFileHandle> }).showSaveFilePicker;
  if (picker) {
    try {
      const handle = await picker({ suggestedName: name });
      const w = await (handle as FileSystemFileHandle).createWritable();
      await w.write(blob);
      await w.close();
      toast('Image saved.', 'ok');
      return;
    } catch (e) {
      if ((e as Error).name === 'AbortError') return;
      /* fall through to anchor download */
    }
  }
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 4000);
  toast('Image saved.', 'ok');
}

async function shareItem(id: string): Promise<void> {
  const item = items.get(id);
  if (!item || item.kind !== 'image' || !item.bytes) return;
  const name = imageFilename(item.name, item.mime, item.at);
  const file = new File([item.bytes as BlobPart], name, { type: item.mime || 'image/jpeg' });
  const nav = navigator as Navigator & { canShare?: (d: unknown) => boolean };
  if (nav.share && nav.canShare?.({ files: [file] })) {
    try {
      await nav.share({ files: [file], title: name });
    } catch {
      /* cancelled */
    }
  } else {
    void saveItem(id);
  }
}

function clearFeed(): void {
  for (const it of items.values()) if (it.blobUrl) URL.revokeObjectURL(it.blobUrl);
  items.clear();
  ui.clearFeed();
  emit('info', 'Feed cleared on this device.');
}

// ── pairing management ──────────────────────────────────────────────────────
function rememberCurrent(): void {
  const p = makePairing(room, pass, peerName || 'Paired device', Date.now());
  pairings = upsertPairing(pairings, p);
  savePairings(store, pairings);
  ui.renderSaved(pairings, room);
}

function connectSaved(id: string): void {
  const p = pairings.find((x) => x.id === id);
  if (!p) return;
  pairings = touchPairing(pairings, p.room, Date.now());
  savePairings(store, pairings);
  room = p.room;
  pass = p.pass;
  writeRoomHash(room, pass, true);
  remember = true;
  ui.setRemember(true);
  store.setItem(LS_REMEMBER, '1');
  emit('info', `Reconnecting to ${p.name}…`);
  reconnect();
}

function forgetSaved(id: string): void {
  pairings = removePairing(pairings, id);
  savePairings(store, pairings);
  ui.renderSaved(pairings, room);
  toast('Pairing forgotten.', 'ok');
}

function disconnect(): void {
  teardownSession();
  ui.setStatus('idle');
  ui.showSession(false);
  ui.showPairing(true);
  emit('warn', 'Disconnected.');
  // Mint a fresh pairing so the QR/link on screen is usable again.
  room = generateRoomId();
  pass = generatePassphrase();
  writeRoomHash(room, pass, true);
  void connect();
}

// ── link helpers ────────────────────────────────────────────────────────────
async function copyLink(): Promise<void> {
  try {
    await navigator.clipboard.writeText(currentInviteUrl(room, pass, true));
    toast('Pairing link copied.', 'ok');
  } catch {
    toast('Could not copy — select the link and copy manually.', 'err');
  }
}

async function shareLink(): Promise<void> {
  const url = currentInviteUrl(room, pass, true);
  if (navigator.share) {
    try {
      await navigator.share({ title: 'Beamwell', text: 'Pair with me on Beamwell', url });
    } catch {
      /* cancelled */
    }
  } else {
    void copyLink();
  }
}

// ── QR ──────────────────────────────────────────────────────────────────────
async function renderQr(url: string): Promise<void> {
  try {
    const dataUrl = await QRCode.toDataURL(url, { margin: 1, width: 320, color: { dark: '#0b0a12', light: '#efeaff' } });
    ui.setQr(dataUrl);
  } catch {
    /* QR is a nicety */
  }
}

async function renderSas(): Promise<void> {
  if (!pass) return;
  try {
    ui.setSas(await deriveSasWords(pass, room));
  } catch {
    /* ignore */
  }
}

// ── camera scanning ─────────────────────────────────────────────────────────
async function startScan(): Promise<void> {
  ui.setScanning(true);
  await scanner.start(
    ui.scanVideo(),
    (value) => {
      ui.setScanning(false);
      const invite = parseInvite(value.includes('#') ? value : `#${value}`);
      if (invite && invite.pass) {
        emit('good', 'Scanned a pairing code.');
        room = invite.room;
        pass = normalizePass(invite.pass);
        writeRoomHash(room, pass, true);
        reconnect();
        toast('Paired from QR.', 'ok');
      } else {
        toast('That QR isn’t a Beamwell pairing code.', 'err');
        emit('warn', 'Scanned a QR that wasn’t a Beamwell pairing.');
      }
    },
    (message) => {
      ui.setScanning(false);
      toast(message, 'err');
      emit('warn', message);
    },
  );
}

function stopScan(): void {
  scanner.stop();
  ui.setScanning(false);
}

// ── helpers ─────────────────────────────────────────────────────────────────
function fmtSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function extFor(mime: string): string {
  return mime === 'image/png' ? 'png' : mime === 'image/webp' ? 'webp' : mime === 'image/gif' ? 'gif' : 'jpg';
}

function safeStorage(): StorageLike {
  try {
    const k = '__beamwell_probe';
    localStorage.setItem(k, '1');
    localStorage.removeItem(k);
    return localStorage;
  } catch {
    const mem = new Map<string, string>();
    return {
      getItem: (key) => mem.get(key) ?? null,
      setItem: (key, v) => void mem.set(key, v),
      removeItem: (key) => void mem.delete(key),
    };
  }
}

// ── automated two-tab test hook ─────────────────────────────────────────────
// The preview browser can't drive a real camera, but two tabs joining the same
// room over the real relays proves discovery + connect + encrypt/decrypt + ack
// end-to-end. Drives the REAL transport and crypto.
function installTestHook(): void {
  (window as unknown as { __beamwellTest?: unknown }).__beamwellTest = {
    room: () => room,
    pass: () => pass,
    status: (): Status | 'connected' => (primaryPeer ? 'connected' : 'waiting'),
    peerName: () => peerName,
    beamText: (t: string) => beam(t),
    sas: () => deriveSasWords(pass, room),
    feed: () =>
      [...items.values()]
        .sort((a, b) => a.at - b.at)
        .map((i) => ({ id: i.id, dir: i.dir, kind: i.kind, text: i.text, state: i.state, size: i.size })),
  };
}
